import { BigNumber } from '@ethersproject/bignumber';
import { MutationFunction, useMutation } from '@tanstack/react-query';
import { Withdrawal } from 'app/hooks/useSiloRouter';
import { SiloAsset } from 'app/queries/decoratedSilo';
import { RefObject, useEffect, useState } from 'react';
import { useThisDecoratedSilo } from '../../silo';
import { ChecksumAddress } from '@silo/types';

export type MutationVariables = { withdrawals: Withdrawal[]; asset: SiloAsset };
export type DoWithdrawals = MutationFunction<void, MutationVariables>;

type UseSubmission = (params: {
  selectedAsset: SiloAsset;
  doWithdrawals: DoWithdrawals;
  normalizedLendingAmount: BigNumber | null;
  normalizedProtectedAmount: BigNumber | null;
  resetLendingAmount: () => void;
  resetProtectedAmount: () => void;
  lendingAmountRef: RefObject<HTMLInputElement>;
  isFullLendingAmount: boolean;
  isFullProtectedAmount: boolean;
}) => {
  submissionFormError?: string;
  isPending: boolean;
  handleSubmit: () => void;
};

export type SubmissionProps = ReturnType<UseSubmission>;

export const useSubmission: UseSubmission = ({
  selectedAsset,
  doWithdrawals,
  normalizedLendingAmount,
  normalizedProtectedAmount,
  resetLendingAmount,
  resetProtectedAmount,
  lendingAmountRef,
  isFullLendingAmount,
  isFullProtectedAmount,
}) => {
  const { siloAddress } = useThisDecoratedSilo();

  const { isLoading, mutate } = useMutation<void, Error, MutationVariables>(doWithdrawals, {
    onSuccess: () => {
      resetLendingAmount();
      resetProtectedAmount();
    },
  });

  const [submissionFormError, setSubmissionFormError] = useState<string | undefined>();

  const handleSubmit = () => {
    if (normalizedLendingAmount || normalizedProtectedAmount) {
      mutate({
        asset: selectedAsset,
        withdrawals: mapFormStateToWithdrawals({
          siloAddress,
          selectedAsset,
          normalizedLendingAmount,
          normalizedProtectedAmount,
          isFullLendingAmount,
          isFullProtectedAmount,
        }),
      });
    } else {
      setSubmissionFormError('Enter an amount');
      lendingAmountRef.current?.focus();
    }
  };

  useEffect(() => {
    if (normalizedLendingAmount || normalizedProtectedAmount) {
      setSubmissionFormError(undefined);
    }
  }, [normalizedLendingAmount, normalizedProtectedAmount]);

  return {
    submissionFormError,
    isPending: isLoading,
    handleSubmit,
  };
};

type MapFormStateToWithdrawals = (params: {
  siloAddress: ChecksumAddress;
  selectedAsset: SiloAsset;
  normalizedLendingAmount: BigNumber | null;
  normalizedProtectedAmount: BigNumber | null;
  isFullLendingAmount: boolean;
  isFullProtectedAmount: boolean;
}) => Withdrawal[];

const mapFormStateToWithdrawals: MapFormStateToWithdrawals = ({
  siloAddress,
  selectedAsset: { assetAddress },
  normalizedLendingAmount,
  normalizedProtectedAmount,
  isFullLendingAmount,
  isFullProtectedAmount,
}) => {
  const withdrawals: Withdrawal[] = [];

  if (normalizedLendingAmount) {
    withdrawals.push({
      siloAddress,
      assetAddress,
      amount: normalizedLendingAmount,
      isCollateralOnly: false,
      isFullAmount: isFullLendingAmount,
    });
  }

  if (normalizedProtectedAmount) {
    withdrawals.push({
      siloAddress,
      assetAddress,
      amount: normalizedProtectedAmount,
      isCollateralOnly: true,
      isFullAmount: isFullProtectedAmount,
    });
  }

  return withdrawals;
};
